<!-- eslint-disable max-len -->
<template>
  <aha-modal
    :title="title"
    @hide="$emit('hide')"
    action-position="justify-center"
  >
    <aha-tabs-primary @update:activeIndex="onChangeTab">
      <aha-tab-item :title="$i18nPresenter.t('Recent')" />
      <aha-tab-item :title="$i18nPresenter.t('All locations')" />
    </aha-tabs-primary>
    <div v-if="arePresentationsLoading" class="flex items-center justify-center h-52">
      <aha-spinner size="lg" variant="tail" />
    </div>

    <template v-if="!arePresentationsLoading">
      <ErrorSlideCopy v-if="hasError" @getAllUserPresentationsAndFolders="getAllUserPresentationsAndFolders" />

      <template v-if="!hasError">
        <NoPresentationSlideCopy v-if="!hasPresentationToCopyTo" />

        <TabRecentSlideCopy
          v-if="isTabRecentVisible"
          :selectedPresentationId="selectedPresentationId"
          :mostRecentPresentations="mostRecentPresentations"
          @handlePresentationItemClick="handlePresentationItemClick"
        />

        <TabAllLocationsSlideCopy
          v-if="isTabAllLocationsVisible"
          :selectedPresentationId="selectedPresentationId"
          :folders="folders"
          :allowToCopyToPresentations="allowToCopyToPresentations"
          @handlePresentationItemClick="handlePresentationItemClick"
        />
      </template>
    </template>

    <InsertAfterSlideCopy
      v-if="isInsertAfterSlideVisible"
      :selectedPresentationId="selectedPresentationId"
      :areSlidesLoading="areSlidesLoading"
      :slides="slides"
      :selectedSlides="selectedSlides"
      @onChangeSelectedSlideOrder="onChangeSelectedSlideOrder"
      @onChangeIncludingResponseData="onChangeIncludingResponseData"
    />

    <ButtonSlideCopy
      v-if="isButtonCopyVisible"
      :selectedPresentationId="selectedPresentationId"
      :isSlideCopying="isSlideCopying"
      @handleCopyButtonClick="() => handleCopyButtonClick(allowToCopyToPresentations)"
    />
  </aha-modal>
</template>

<script>
import { mapState } from 'vuex'
import * as Sentry from '@sentry/vue'
import Utils from '@/util'
import Config from '@/config'

// api
import FolderApi from '@/api/folder'
import PresentationApi from '@/api/presentation'

// components
import NoPresentationSlideCopy from '@/components-v2/common/slide-copy/NoPresentationSlideCopy.vue'
import ErrorSlideCopy from '@/components-v2/common/slide-copy/ErrorSlideCopy.vue'
import TabRecentSlideCopy from '@/components-v2/common/slide-copy/TabRecentSlideCopy.vue'
import TabAllLocationsSlideCopy from '@/components-v2/common/slide-copy/TabAllLocationsSlideCopy.vue'
import InsertAfterSlideCopy from '@/components-v2/common/slide-copy/InsertAfterSlideCopy.vue'
import ButtonSlideCopy from '@/components-v2/common/slide-copy/ButtonSlideCopy.vue'

// trackers
import SlideMixpanelTracker from '@/utils/mixpanel/slide.mixpanel-tracker'
import PresentationMixpanelTracker from '@/utils/mixpanel/presentation.mixpanel-tracker'

/**
 * @deprecated Use SlideCopyV2 instead
 */
export default {
  components: {
    NoPresentationSlideCopy,
    ErrorSlideCopy,
    TabRecentSlideCopy,
    TabAllLocationsSlideCopy,
    InsertAfterSlideCopy,
    ButtonSlideCopy,
  },
  props: {
    getItems: {
      type: Function,
      default: () => [],
    },
    title: {
      type: String,
      default: null,
    },
  },
  data: () => {
    return {
      slides: [],
      folders: [],
      hasError: false,
      isSlideCopying: false,
      selectedSlideOrder: 0,
      areSlidesLoading: false,
      selectedPresentationId: null,
      isIncludingResponseData: true,
      arePresentationsLoading: false,
      allowToCopyToPresentations: [],
      mostRecentPresentations: [],
      selectedSlides: [],
      currentActiveTabIndex: 0,
    }
  },
  computed: {
    ...mapState({
      presentation: (state) => state.presentation,
      slideActive: (state) => state.slideActive,
    }),
    hasPresentationToCopyTo() {
      return this.allowToCopyToPresentations.length > 0
    },
    isTabRecentVisible() {
      return this.hasPresentationToCopyTo && this.currentActiveTabIndex === 0
    },
    isTabAllLocationsVisible() {
      return this.hasPresentationToCopyTo && this.currentActiveTabIndex === 1
    },
    isInsertAfterSlideVisible() {
      return !this.arePresentationsLoading && !this.hasError && this.hasPresentationToCopyTo
    },
    isButtonCopyVisible() {
      return !this.hasError && this.allowToCopyToPresentations.length !== 0 &&
        !this.arePresentationsLoading && this.hasPresentationToCopyTo
    },
  },
  async mounted() {
    this.selectedSlides = this.getItems()
    const userSettings = this.$store.state.currentUser.rememberSettings
    if (userSettings && userSettings.isIncludingResponseData !== undefined) {
      this.isIncludingResponseData = userSettings.isIncludingResponseData
    } else {
      this.isIncludingResponseData = true
    }
    this.getAllUserPresentationsAndFolders()
  },
  methods: {
    onChangeTab(tabIndex) {
      this.currentActiveTabIndex = tabIndex
      this.selectedPresentationId = null
    },
    onChangeSelectedSlideOrder(value) {
      this.selectedSlideOrder = value
    },
    onChangeIncludingResponseData(value) {
      this.isIncludingResponseData = value
    },
    async getAndSetFolders() {
      const folderTree = await FolderApi.getFullFolderTree()
      this.folders = this._flattenFolderTree(folderTree)
      this.folders.unshift({ folderId: null, folderName: this.$i18nPresenter.t('myPresentations') })
    },
    _flattenFolderTree(folderTree) {
      return folderTree.reduce((acc, {
        folderId, folderName, folders, depth,
      }) => {
        acc.push({
          folderId, folderName, depth,
        })
        if (folders && folders.length > 0) {
          acc = acc.concat(this._flattenFolderTree(folders))
        }
        return acc
      }, [])
    },
    async getAllUserPresentationsAndFolders() {
      this.hasError = false
      this.arePresentationsLoading = true
      const isFromWorkspace = this.$store.state.workingMode === 'team'
      try {
        const { presentations } = await PresentationApi.getPresentations({
          limit: 500,
          includeShared: true,
          fromWorkspace: isFromWorkspace,
        })

        // exclude current presentation
        this.allowToCopyToPresentations = presentations.filter((presentation) => {
          return presentation.id !== this.$store.state.presentation.id
        })

        if (this.allowToCopyToPresentations.length === 0) {
          this.arePresentationsLoading = false
          return
        }

        // get 10 most recent presentations
        const sortedPresentationLatest = this.allowToCopyToPresentations
          .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
        this.mostRecentPresentations = sortedPresentationLatest.slice(0, 10)

        // get slides by presentation id
        if (this.selectedPresentationId) {
          this._getSlidesByPresentationId()
        }

        // get folder list
        await this.getAndSetFolders()
      } catch (error) {
        Sentry.captureException(error)
        this.hasError = true
        Utils.mixpanelTracking('Presentation Editing', 'Copy slide', 'Error when loading presentations')

        throw error
      } finally {
        this.arePresentationsLoading = false
      }
    },
    async _getSlidesByPresentationId() {
      // this.selectedPresentationId = this.allowToCopyToPresentations[0].id
      const res = await this.$http.get(Config.api.getSlides, {
        params: {
          presentationId: this.selectedPresentationId,
        },
      })

      this.slides = res.body
      this.selectedSlideOrder = 0
    },
    handleCheckboxClick() {
      this.isIncludingResponseData = !this.isIncludingResponseData
    },
    trackCopySlide(copiedSlideIds, newSlideIds) {
      const presentationTracker = new PresentationMixpanelTracker(this.presentation)
      const slideTracker = new SlideMixpanelTracker(this.presentation, this.$route.name)
      slideTracker.setCustomProps({
        ...presentationTracker.getPresentationContext(),
        copiedSlideIds,
        slideId: newSlideIds,
      })
      slideTracker.trackCreateNewSlide('Side left > Slide menu > Copy to', null)
    },
    async handleCopyButtonClick(allowToCopyToPresentations) {
      if (this.isSlideCopying) return

      const payload = {
        duplicatedSlideId: this.selectedSlides.map((item) => item.id),
        insertAfter: this.selectedSlideOrder,
        isDuplicatingToOtherPresentation: true,
        isIncludingResponseData: this.isIncludingResponseData,
        destinatedPresentationId: this.selectedPresentationId,
      }

      this.isSlideCopying = true
      const { accessCode, name } = allowToCopyToPresentations
        .find((item) => item.id === this.selectedPresentationId)

      try {
        const duplicateResult = await this.$http.post(Config.api.duplicateSlideToOtherPresentation, payload)
        const slideIds = duplicateResult && duplicateResult.body &&
          duplicateResult.body.slides && duplicateResult.body.slides.map((item) => item.id)
        this.trackCopySlide(payload.duplicatedSlideId, slideIds)
        const toastMessage = this.selectedSlides.length === 1
          ? this.$i18nPresenter.t('Copy 1 slide successfully')
          : `${this.$i18nPresenter.t('Copy')} ${this.selectedSlides.length} ${this.$i18nPresenter.t('slides successfully')}`
        Utils.showToastSuccess(`${toastMessage} ${this.$i18nPresenter.t('to presentation')} "${name}" [${accessCode}]`)
      } catch (error) {
        Sentry.captureException(error)
        Utils.handleError(error.body.message)
        Utils.showToastError(this.$i18nPresenter.t('Failed to copy slide.'))
        return
      } finally {
        this.isSlideCopying = false
      }
      await this._updateUser()
      this.closeSlideCopyPopover()
      this.$emit('hide')
    },

    async _updateUser() {
      try {
        const payload = {
          isIncludingResponseData: this.isIncludingResponseData,
        }
        const result = await this.$http
          .put(Config.api.updateUserSettings, payload)
        this.$store.state.currentUser.rememberSettings
        = result.body.rememberSettings
      } catch (error) {
        Sentry.captureException(error)
      }
    },
    closeSlideCopyPopover() {
      const newSlides = this.$store.state.presentation.slides.map((item) => {
        return { ...item, isSlideCopyPopoverShowing: false }
      })

      this.$store.state.presentation.slides = newSlides
    },
    async handlePresentationItemClick(presentation) {
      // won't handle event if click on the same presentation
      if (this.selectedPresentationId === presentation.id) {
        return
      }

      this.hasError = false
      this.selectedSlideOrder = -1
      this.areSlidesLoading = true

      this.selectedPresentationId = presentation.id

      try {
        const res = await this.$http.get(Config.api.getSlides, {
          params: {
            presentationId: this.selectedPresentationId,
          },
        })
        this.slides = res.body
        this.selectedSlideOrder = 0
      } catch (error) {
        Sentry.captureException(error)
        this.hasError = true
        Utils.mixpanelTracking('Presentation Editing', 'Copy slide', 'Error when loading slides')
      }

      this.areSlidesLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.aha-tabs {
  border-bottom: 1px solid rgb(235, 235, 235); // base-30
  @apply mb-4;
  &::v-deep .aha-tab-title {
    @apply py-2;
  }
}
</style>
